import Microgame from '../engine/Microgame.js';

export default class FishGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Pêche !`, en: `Fish!`, es: `¡Pesca!` },
            controls: { fr: `Appuie quand le flotteur s'enfonce`, en: `Tap when the bobber sinks`, es: `Toca cuando el cebo se hunda` },
            objective: { fr: `Ferres le poisson avec un timing parfait`, en: `Reel in the fish with perfect timing`, es: `Engancha el pez en el momento exacto` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M3 3s4 0 7 7c3 7 11 11 11 11"/><path d="M12 15s3 3 7 3c-4 0-7-3-7-3Z"/></svg>`,
            actions: [
                { type: 'wait', duration: 1280 },
                { type: 'tap', x: 50, y: 62, duration: 220 },
                { type: 'wait', duration: 760 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.bobber = null;
        this.water = null;
        this.fish = null;
        this.state = 'waiting'; // waiting, fake, biting, caught
        this.timeline = []; // Schedule of fakes and bites
        this.biteEndTime = 0;
        this.reactionWindow = 600; // ms to react when bobber sinks
        this.fishSide = this.isPlayback ? 1 : (Math.random() > 0.5 ? 1 : -1); // Random side fish comes from

        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeedScale = 1 + (speedMult - 1) * 0.4;
        this.duration = 3500 * (speedMult / adjustedSpeedScale);
        this.reactionWindow = Math.max(400, 750 - (speedMult - 1) * 100); // x1=750ms, x4=450ms

        this._isAnimating = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "CATCH!";

        // Sky gradient
        const sky = document.createElement('div');
        sky.style.position = 'absolute';
        sky.style.width = '100%';
        sky.style.height = '60%';
        sky.style.top = '0';
        sky.style.background = `linear-gradient(180deg, ${this.darken(this.secondaryColor, 0.15)} 0%, ${this.darken(this.secondaryColor, 0.3)} 100%)`;
        this.container.appendChild(sky);

        // Stars
        for (let i = 0; i < 8; i++) {
            const star = document.createElement('div');
            star.style.position = 'absolute';
            star.style.width = `${2 + Math.random() * 3}px`;
            star.style.height = star.style.width;
            star.style.background = `${this.primaryColor}`;
            star.style.borderRadius = '50%';
            star.style.top = `${5 + Math.random() * 45}%`;
            star.style.left = `${5 + Math.random() * 90}%`;
            star.style.opacity = `${0.3 + Math.random() * 0.5}`;
            this.container.appendChild(star);
        }

        // Water (wavy surface)
        this.water = document.createElement('div');
        this.water.style.position = 'absolute';
        this.water.style.width = '100%';
        this.water.style.height = '42%';
        this.water.style.bottom = '0';
        this.water.style.background = `linear-gradient(180deg, ${this.secondaryColor}99 0%, ${this.secondaryColor}33 100%)`;
        this.water.style.borderRadius = '50% 50% 0 0 / 8px 8px 0 0';
        this.container.appendChild(this.water);

        // Water surface wave line
        const waveLine = document.createElement('div');
        waveLine.style.position = 'absolute';
        waveLine.style.width = '120%';
        waveLine.style.height = '4px';
        waveLine.style.top = '-2px';
        waveLine.style.left = '-10%';
        waveLine.style.background = this.secondaryColor;
        waveLine.style.borderRadius = '2px';
        waveLine.style.opacity = '0.6';
        this.water.appendChild(waveLine);

        // Fishing Line
        this.line = document.createElement('div');
        this.line.style.position = 'absolute';
        this.line.style.width = '2px';
        this.line.style.height = '250px';
        this.line.style.background = `${this.primaryColor}88`;
        this.line.style.bottom = '38%';
        this.line.style.left = '50%';
        this.line.style.transformOrigin = 'top center';
        this.container.appendChild(this.line);

        // Bobber (detailed)
        this.bobber = document.createElement('div');
        this.bobber.style.position = 'absolute';
        this.bobber.style.width = '36px';
        this.bobber.style.height = '36px';
        this.bobber.style.bottom = '37%';
        this.bobber.style.left = '50%';
        this.bobber.style.transform = 'translateX(-50%)';
        this.bobber.style.cursor = 'pointer';
        this.bobber.style.zIndex = '5';
        this.container.appendChild(this.bobber);

        // Bobber top (primary sphere)
        const bobTop = document.createElement('div');
        bobTop.style.position = 'absolute';
        bobTop.style.width = '24px';
        bobTop.style.height = '24px';
        bobTop.style.background = this.primaryColor;
        bobTop.style.borderRadius = '50%';
        bobTop.style.top = '0';
        bobTop.style.left = '50%';
        bobTop.style.transform = 'translateX(-50%)';
        bobTop.style.boxShadow = `0 0 15px ${this.primaryColor}88`;
        this.bobber.appendChild(bobTop);

        // Bobber bottom (white stripe)
        const bobBot = document.createElement('div');
        bobBot.style.position = 'absolute';
        bobBot.style.width = '18px';
        bobBot.style.height = '14px';
        bobBot.style.background = `${this.primaryColor}bb`;
        bobBot.style.borderRadius = '0 0 50% 50%';
        bobBot.style.bottom = '0';
        bobBot.style.left = '50%';
        bobBot.style.transform = 'translateX(-50%)';
        this.bobber.appendChild(bobBot);

        // Bobber stick
        const stick = document.createElement('div');
        stick.style.position = 'absolute';
        stick.style.width = '3px';
        stick.style.height = '14px';
        stick.style.background = `${this.primaryColor}99`;
        stick.style.top = '-12px';
        stick.style.left = '50%';
        stick.style.transform = 'translateX(-50%)';
        stick.style.borderRadius = '2px';
        this.bobber.appendChild(stick);

        // Ripple rings container
        this.ripples = document.createElement('div');
        this.ripples.style.position = 'absolute';
        this.ripples.style.width = '100%';
        this.ripples.style.height = '100%';
        this.ripples.style.bottom = '0';
        this.ripples.style.left = '0';
        this.ripples.style.zIndex = '4';
        this.ripples.style.pointerEvents = 'none';
        this.container.appendChild(this.ripples);

        const speedMult = this.engine.speedMultiplier || 1.0;
        const actualTime = this.duration / speedMult;

        this.gameStartTime = Date.now();
        this.actualGameTime = actualTime;

        this.scheduleNextEvents(Date.now(), actualTime);

        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);

        this.animationId = null;
        this.animate();
    }

    scheduleNextEvents(now, remainingTime) {
        this.timeline = [];
        if (this.isPlayback) {
            this.timeline.push({ type: 'bite', time: now + 1200 });
            return;
        }

        // Latest time a bite can occur and still leave reactionWindow + 100ms
        const latestBite = Math.max(150, remainingTime - 700);

        // Truly random bite time anywhere from start to latest possible
        let biteDelay = 150 + Math.random() * (latestBite - 150);

        // 40% chance of ONE fake, but only if there is enough room before the bite
        if (biteDelay > 600 && Math.random() > 0.6) {
            // Place the fake randomly before the bite (at least 450ms before)
            const fakeDelay = 150 + Math.random() * (biteDelay - 550);
            this.timeline.push({ type: 'fake', time: now + fakeDelay });
        }

        this.timeline.push({ type: 'bite', time: now + biteDelay });
    }

    darken(hex, factor) {
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = hex;
        const match = ctx.fillStyle.match(/\d+/g);
        if (!match) return hex;
        return `rgb(${Math.floor(match[0] * factor)},${Math.floor(match[1] * factor)},${Math.floor(match[2] * factor)})`;
    }

    animate() {
        if (!this.active) return;
        const now = Date.now();

        // Process timeline events
        for (let i = 0; i < this.timeline.length; i++) {
            const ev = this.timeline[i];
            if (ev.time <= now) {
                if (ev.type === 'fake') {
                    this.timeline.splice(i, 1);
                    i--;
                    this.triggerFake();
                } else if (ev.type === 'bite') {
                    this.timeline.splice(i, 1);
                    i--;
                    this.triggerBite(now);
                }
            }
        }

        // Idle bobbing
        if (this.state === 'waiting') {
            const offset = Math.sin(now / 250) * 5;
            const tilt = Math.sin(now / 400) * 3;
            this.bobber.style.bottom = `calc(37% + ${offset}px)`;
            this.bobber.style.transform = `translateX(-50%) rotate(${tilt}deg)`;
        }

        // Releasing loop
        if (this.state === 'biting' && now > this.biteEndTime) {
            this.state = 'waiting';
            if (this.shakeAnim) this.shakeAnim.cancel();

            this.bobber.style.transition = 'bottom 0.4s ease-out, opacity 0.3s ease-in';
            this.bobber.style.bottom = '37%';
            this.bobber.style.opacity = '1';
            setTimeout(() => {
                if (this.state === 'waiting' && this.bobber) this.bobber.style.transition = '';
            }, 400);

            // Reschedule
            const elapsed = now - this.gameStartTime;
            const remaining = this.actualGameTime - elapsed;
            if (remaining > 1000) {
                this.scheduleNextEvents(now, remaining);
            }
        }

        this.animationId = requestAnimationFrame(() => this.animate());
    }

    update(dt) {
        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }
    }

    triggerFake() {
        if (this.state !== 'waiting') return;
        this.state = 'fake';

        // Quick dip
        this.bobber.style.transition = 'bottom 0.1s ease-out';
        this.bobber.style.bottom = '32%'; // not fully submerged

        // Tiny ripple
        const ripple = document.createElement('div');
        ripple.style.position = 'absolute';
        ripple.style.width = '12px';
        ripple.style.height = '6px';
        ripple.style.border = `1px solid ${this.secondaryColor}`;
        ripple.style.borderRadius = '50%';
        ripple.style.left = '50%';
        ripple.style.bottom = '38%';
        ripple.style.transform = 'translateX(-50%) scale(0)';
        ripple.style.pointerEvents = 'none';
        this.ripples.appendChild(ripple);

        ripple.animate([
            { transform: 'translateX(-50%) scale(0)', opacity: 0.6 },
            { transform: 'translateX(-50%) scale(3)', opacity: 0 }
        ], { duration: 350, easing: 'ease-out' });
        setTimeout(() => ripple.remove(), 350);

        // Snap back up
        setTimeout(() => {
            if (this.state === 'fake' && this.bobber) {
                this.bobber.style.transition = 'bottom 0.2s ease-in-out';
                this.bobber.style.bottom = '37%';

                setTimeout(() => {
                    if (this.state === 'fake') {
                        this.state = 'waiting';
                        if (this.bobber) this.bobber.style.transition = '';
                    }
                }, 200);
            }
        }, 120);
    }

    triggerBite(now) {
        if (this.state !== 'waiting' && this.state !== 'fake') return;
        this.state = 'biting';
        this.biteEndTime = now + this.reactionWindow;
        // Bobber sinks deep and disappears
        this.bobber.style.transition = 'bottom 0.15s ease-in, opacity 0.15s ease-out';
        this.bobber.style.bottom = '20%'; 
        this.bobber.style.opacity = '0';

        // Big splash ripples
        for (let i = 0; i < 3; i++) {
            setTimeout(() => this.createRipple(), i * 100);
        }

        // Violent shaking
        this.shakeAnim = this.bobber.animate([
            { transform: 'translateX(-50%) rotate(-8deg)' },
            { transform: 'translateX(-50%) rotate(8deg)' },
            { transform: 'translateX(-50%) rotate(-6deg)' },
            { transform: 'translateX(-50%) rotate(6deg)' },
            { transform: 'translateX(-50%) rotate(0deg)' }
        ], { duration: 300, iterations: Infinity });

        const vibrate = navigator.vibrate || navigator.webkitVibrate || navigator.mozVibrate || navigator.msVibrate;
        if (vibrate) navigator.vibrate(200); // Small haptic feedback if mobile
    }

    createRipple() {
        const ripple = document.createElement('div');
        ripple.style.position = 'absolute';
        ripple.style.width = '20px';
        ripple.style.height = '10px';
        ripple.style.border = `2px solid ${this.secondaryColor}`;
        ripple.style.borderRadius = '50%';
        ripple.style.left = '50%';
        ripple.style.bottom = '38%';
        ripple.style.transform = 'translateX(-50%) scale(0)';
        ripple.style.pointerEvents = 'none';
        this.ripples.appendChild(ripple);

        ripple.animate([
            { transform: 'translateX(-50%) scale(0)', opacity: 1 },
            { transform: 'translateX(-50%) scale(4)', opacity: 0 }
        ], { duration: 600, easing: 'ease-out' });

        setTimeout(() => ripple.remove(), 600);
    }

    onTap() {
        if (this.won || this.lost || this.state === 'caught') return;

        if (this.state === 'biting') {
            this.state = 'caught';
            this._isAnimating = true;
            this._frozenTimerValue = this.engine.timer;

            const speedMult = this.engine.speedMultiplier || 1.0;
            const animDur = 500 / speedMult;

            // Pull up bobber  
            this.bobber.style.transition = `bottom ${0.5 / speedMult}s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.1s`;
            this.bobber.style.bottom = '80%';
            this.bobber.style.opacity = '1';
            this.bobber.getAnimations().forEach(a => a.cancel());

            // Fish appears! Coming from random side
            const fish = document.createElement('div');
            fish.style.position = 'absolute';
            fish.style.width = '70px';
            fish.style.height = '40px';
            fish.style.left = '50%';
            fish.style.bottom = '28%';
            fish.style.transform = `translateX(-50%) scaleX(${this.fishSide})`;
            fish.style.zIndex = '6';
            this.container.appendChild(fish);

            // Fish body
            const body = document.createElement('div');
            body.style.position = 'absolute';
            body.style.width = '50px';
            body.style.height = '30px';
            body.style.background = `radial-gradient(ellipse at 40% 40%, ${this.primaryColor}, ${this.darken(this.primaryColor, 0.5)})`;
            body.style.borderRadius = '60% 40% 40% 60%';
            body.style.top = '5px';
            body.style.left = '0';
            fish.appendChild(body);

            // Fish tail
            const tail = document.createElement('div');
            tail.style.position = 'absolute';
            tail.style.width = '0';
            tail.style.height = '0';
            tail.style.borderTop = `12px solid transparent`;
            tail.style.borderBottom = `12px solid transparent`;
            tail.style.borderLeft = `20px solid ${this.darken(this.primaryColor, 0.7)}`;
            tail.style.right = '-8px';
            tail.style.top = '8px';
            fish.appendChild(tail);

            // Fish eye
            const eye = document.createElement('div');
            eye.style.position = 'absolute';
            eye.style.width = '8px';
            eye.style.height = '8px';
            eye.style.background = this.secondaryColor;
            eye.style.borderRadius = '50%';
            eye.style.top = '10px';
            eye.style.left = '10px';
            body.appendChild(eye);

            const pupil = document.createElement('div');
            pupil.style.position = 'absolute';
            pupil.style.width = '4px';
            pupil.style.height = '4px';
            pupil.style.background = this.darken(this.primaryColor, 0.2);
            pupil.style.borderRadius = '50%';
            pupil.style.top = '2px';
            pupil.style.left = '2px';
            eye.appendChild(pupil);

            // Fish swing animation upward
            fish.animate([
                { bottom: '28%', transform: `translateX(-50%) scaleX(${this.fishSide}) rotate(0deg)` },
                { bottom: '75%', transform: `translateX(-50%) scaleX(${this.fishSide}) rotate(-20deg)` }
            ], { duration: animDur, easing: 'ease-out', fill: 'forwards' });

            // Water splash
            for (let i = 0; i < 5; i++) {
                setTimeout(() => this.createRipple(), i * (80 / speedMult));
            }

            // Droplets flying up
            for (let i = 0; i < 8; i++) {
                const drop = document.createElement('div');
                drop.style.position = 'absolute';
                drop.style.width = `${3 + Math.random() * 5}px`;
                drop.style.height = drop.style.width;
                drop.style.background = `${this.secondaryColor}aa`;
                drop.style.borderRadius = '50%';
                drop.style.left = `${45 + Math.random() * 10}%`;
                drop.style.bottom = '38%';
                drop.style.zIndex = '7';
                this.container.appendChild(drop);

                const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.5;
                const dist = 60 + Math.random() * 80;
                drop.animate([
                    { transform: 'scale(1)', opacity: 1, bottom: '38%' },
                    { transform: 'scale(0.5)', opacity: 0, bottom: `calc(38% + ${Math.sin(angle) * dist}px)`, left: `calc(${parseFloat(drop.style.left)}% + ${Math.cos(angle) * dist}px)` }
                ], { duration: animDur + Math.random() * (200 / speedMult), easing: 'ease-out' });
            }

            // Win shortly after the animation finishes
            setTimeout(() => this.win(), animDur + 100);

        } else if (this.state === 'waiting' || this.state === 'fake') {
            // Tapped too early (or tapped ON A FAKE!)
            this.lose();
            this.bobber.style.transition = 'opacity 0.3s';
            this.bobber.style.opacity = '0.3';
        }
    }

    cleanup() {
        super.cleanup();
        if (this.animationId) cancelAnimationFrame(this.animationId);
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
